/**
 * F-17-bg / F-17-color palette state — what the F-27 background- and
 * text-color actions OFFER (preset swatches) and what they show for the
 * current selection (mixed-fill and mixed-opacity previews).
 *
 * The hex normal form itself (`normalizeHexColor`, `opacityOf`,
 * `withOpacity`) lives in core/color.ts, next to the readability math that
 * has to agree with it.
 */
import { opacityOf } from "../../core/color.js";

/**
 * The 8 pastel presets (F-27 cell actions v1). Stored verbatim as the cell's
 * `bg` value — lowercase #rrggbb, same normal form `normalizeHexColor`
 * produces for custom input, so preset-vs-custom equality is plain string
 * comparison.
 */
export const BG_PRESETS: readonly string[] = [
  "#fecaca", // red
  "#fed7aa", // orange
  "#fef08a", // yellow
  "#bbf7d0", // green
  "#a5f3fc", // cyan
  "#bfdbfe", // blue
  "#e9d5ff", // purple
  "#e5e7eb", // gray
];

/**
 * Text-color presets (F-17 `color`). Saturated rather than pastel — these are
 * INK, and each clears WCAG AA against both theme surfaces on its own, so
 * picking one is never a way to make a cell unreadable. "No explicit color"
 * is deliberately not in this list: it is the separate Auto button, which
 * hands the cell back to the automatic readable-ink derivation
 * (core/color.ts `readableTextColor`).
 */
export const TEXT_PRESETS: readonly string[] = [
  "#111827", // near-black
  "#6b7280", // gray
  "#b42318", // red
  "#b45309", // amber
  "#15803d", // green
  "#0369a1", // blue
  "#6d28d9", // purple
  "#be185d", // pink
];

/** What the toolbar's swatch button previews for the current selection. */
export type SwatchPreview =
  | { kind: "none" } // every cell unset
  | { kind: "color"; color: string } // uniform value
  | { kind: "mixed" }; // differing values → neutral swatch

/**
 * Fold the selection's per-cell colors (as read via getCellBg / getCellColor —
 * `undefined` = unset) into the swatch preview. Comparison is exact string
 * equality: all locally-written values share `normalizeHexColor`'s normal
 * form, so e.g. `#FFAA00` vs `#ffaa00` (foreign writer) reads as mixed —
 * acceptable, the swatch is a preview, not a validator.
 */
export function swatchPreview(colors: Iterable<string | undefined>): SwatchPreview {
  let first: string | undefined;
  let seen = false;
  for (const color of colors) {
    if (!seen) {
      first = color;
      seen = true;
    } else if (color !== first) {
      return { kind: "mixed" };
    }
  }
  if (first === undefined) return { kind: "none" };
  return { kind: "color", color: first };
}

/** What the opacity slider shows for the current selection. */
export type OpacityPreview =
  | { kind: "none" } // nothing filled — opacity has nothing to act on
  | { kind: "value"; pct: number } // every filled cell at the same opacity
  | { kind: "mixed" }; // filled cells disagree

/**
 * Fold the selection's backgrounds into the slider state. UNFILLED CELLS ARE
 * IGNORED rather than counted as a disagreement: opacity is a property OF a
 * fill, so a range that is half-filled at 60% still reads 60% — and dragging
 * still moves only the filled half (session.setCellsBgOpacity).
 */
export function opacityPreview(bgs: Iterable<string | undefined>): OpacityPreview {
  let first: number | undefined;
  for (const bg of bgs) {
    if (bg === undefined) continue;
    const pct = opacityOf(bg);
    if (first === undefined) first = pct;
    else if (pct !== first) return { kind: "mixed" };
  }
  return first === undefined ? { kind: "none" } : { kind: "value", pct: first };
}
